// Toplu dolap uretim dialog'u — bir noktada ardisik numarali N adet dolap olustur.

import { useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Boxes, X } from 'lucide-react';
import { Buton, Etiket, Girdi, Rozet, toast } from '@emanetx/ui';
import { useDolapTopluUret } from './api.js';

type Boyut = 'KUCUK' | 'ORTA' | 'BUYUK' | 'EKSTRA';

export function TopluUretDialog({
  acik,
  onDegisim,
  noktaId,
  noktaKod,
  noktaAd,
}: {
  acik: boolean;
  onDegisim: (acik: boolean) => void;
  noktaId: string;
  noktaKod: string;
  noktaAd: string;
}) {
  const [adet, setAdet] = useState(10);
  const [baslangicNo, setBaslangicNo] = useState(1);
  const [basamak, setBasamak] = useState(2);
  const [boyut, setBoyut] = useState<Boyut>('ORTA');
  const [kiralamaSaati, setKiralamaSaati] = useState(4);
  const [periyotUcreti, setPeriyotUcreti] = useState(50);
  const [saatlikUcret, setSaatlikUcret] = useState(10);
  const [cezaUcreti, setCezaUcreti] = useState(100);

  const uret = useDolapTopluUret();

  const onayla = async () => {
    try {
      const r = await uret.mutateAsync({
        noktaId,
        adet,
        baslangicNo,
        basamak,
        boyut,
        kiralamaSaati,
        periyotUcreti,
        saatlikUcret,
        cezaUcreti,
      });
      if (r.atlanan.length > 0) {
        toast.success(
          `${r.olusturulan} dolap üretildi · ${r.atlanan.length} numara zaten kayıtlı (atlandı)`,
        );
      } else {
        toast.success(`${r.olusturulan} dolap üretildi`);
      }
      onDegisim(false);
    } catch (err) {
      toast.error((err as Error).message);
    }
  };

  // Onizleme
  const onizleme: string[] = [];
  for (let i = 0; i < Math.min(adet, 5); i++) {
    onizleme.push(String(baslangicNo + i).padStart(basamak, '0'));
  }
  if (adet > 5) onizleme.push(`… ${String(baslangicNo + adet - 1).padStart(basamak, '0')}`);

  return (
    <Dialog.Root open={acik} onOpenChange={onDegisim}>
      <Dialog.Portal>
        <Dialog.Overlay className="bg-fume-900/50 data-[state=open]:animate-in data-[state=open]:fade-in-0 fixed inset-0 z-40 backdrop-blur-sm" />
        <Dialog.Content className="dark:bg-fume-900 fixed left-1/2 top-1/2 z-50 w-[94vw] max-w-lg -translate-x-1/2 -translate-y-1/2 rounded-2xl bg-white p-6 shadow-2xl">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <Dialog.Title className="text-fume-900 dark:text-fume-50 flex items-center gap-2 text-base font-semibold">
                <Boxes size={16} className="text-marka-500" /> Toplu Dolap Üret
              </Dialog.Title>
              <Dialog.Description asChild>
                <div className="text-fume-500 dark:text-fume-400 mt-1.5 flex items-center gap-2 text-sm">
                  <span className="truncate">{noktaAd}</span>
                  <Rozet ton="fume" className="font-mono">
                    {noktaKod}
                  </Rozet>
                </div>
              </Dialog.Description>
            </div>
            <Dialog.Close asChild>
              <button
                aria-label="Kapat"
                className="text-fume-400 hover:text-fume-700 dark:hover:text-fume-100"
              >
                <X size={18} />
              </button>
            </Dialog.Close>
          </div>

          <div className="mt-6 space-y-4">
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <Etiket htmlFor="adet">Adet</Etiket>
                <Girdi
                  id="adet"
                  type="number"
                  min={1}
                  max={200}
                  value={adet}
                  onChange={(e) => setAdet(Math.max(1, Math.min(200, Number(e.target.value) || 1)))}
                />
              </div>
              <div className="space-y-1.5">
                <Etiket htmlFor="baslangic">Başlangıç No</Etiket>
                <Girdi
                  id="baslangic"
                  type="number"
                  min={1}
                  max={9999}
                  value={baslangicNo}
                  onChange={(e) =>
                    setBaslangicNo(Math.max(1, Math.min(9999, Number(e.target.value) || 1)))
                  }
                />
              </div>
              <div className="space-y-1.5">
                <Etiket htmlFor="basamak">Basamak</Etiket>
                <Girdi
                  id="basamak"
                  type="number"
                  min={1}
                  max={5}
                  value={basamak}
                  onChange={(e) =>
                    setBasamak(Math.max(1, Math.min(5, Number(e.target.value) || 2)))
                  }
                />
              </div>
            </div>

            <div className="border-marka-500/30 bg-marka-500/10 rounded-md border px-3 py-2 text-xs">
              <span className="text-fume-600 dark:text-fume-300">Önizleme: </span>
              <span className="font-mono font-semibold">{onizleme.join(', ')}</span>
              <span className="text-fume-500"> ({adet} dolap)</span>
            </div>

            <div className="space-y-1.5">
              <Etiket htmlFor="boyut">Boyut</Etiket>
              <select
                id="boyut"
                value={boyut}
                onChange={(e) => setBoyut(e.target.value as Boyut)}
                className="border-fume-200 focus:ring-marka-500 dark:border-fume-700 dark:bg-fume-900 dark:text-fume-50 h-11 w-full rounded-lg border bg-white px-3 text-sm focus:outline-none focus:ring-2"
              >
                <option value="KUCUK">Küçük (S)</option>
                <option value="ORTA">Orta (M)</option>
                <option value="BUYUK">Büyük (L)</option>
                <option value="EKSTRA">Ekstra (XL)</option>
              </select>
            </div>

            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <div className="space-y-1.5">
                <Etiket htmlFor="kSaat">Periyot (sa)</Etiket>
                <Girdi
                  id="kSaat"
                  type="number"
                  min={1}
                  value={kiralamaSaati}
                  onChange={(e) => setKiralamaSaati(Number(e.target.value) || 4)}
                />
              </div>
              <div className="space-y-1.5">
                <Etiket htmlFor="pUcret">Periyot ücret</Etiket>
                <Girdi
                  id="pUcret"
                  type="number"
                  step="0.01"
                  value={periyotUcreti}
                  onChange={(e) => setPeriyotUcreti(Number(e.target.value) || 0)}
                />
              </div>
              <div className="space-y-1.5">
                <Etiket htmlFor="sUcret">Saatlik aşım</Etiket>
                <Girdi
                  id="sUcret"
                  type="number"
                  step="0.01"
                  value={saatlikUcret}
                  onChange={(e) => setSaatlikUcret(Number(e.target.value) || 0)}
                />
              </div>
              <div className="space-y-1.5">
                <Etiket htmlFor="cUcret">Ceza</Etiket>
                <Girdi
                  id="cUcret"
                  type="number"
                  step="0.01"
                  value={cezaUcreti}
                  onChange={(e) => setCezaUcreti(Number(e.target.value) || 0)}
                />
              </div>
            </div>

            <div className="flex flex-col-reverse gap-2 pt-2 sm:flex-row sm:justify-end">
              <Dialog.Close asChild>
                <Buton stil="cizgili" disabled={uret.isPending}>
                  Vazgeç
                </Buton>
              </Dialog.Close>
              <Buton onClick={() => void onayla()} disabled={uret.isPending}>
                {uret.isPending ? 'Üretiliyor…' : `${adet} Dolap Üret`}
              </Buton>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
